<template>
  <div class="user-profile-page">
    <!-- ==================== LOADING STATE ==================== -->
    <div
      v-if="isLoading"
      class="profile-loading text-center py-5"
    >
      <b-spinner
        variant="primary"
        type="grow"
        label="Loading..."
      />
      <p class="mt-2 text-muted">
        Loading your profile...
      </p>
    </div>

    <div v-else>
      <!-- ==================== PROFILE HEADER ==================== -->
      <ProfileHeader
        :form="form"
        :initials="initials"
        :avatar-preview="avatarPreview"
        :is-uploading-avatar="isUploadingAvatar"
        :profile-qr-value="profileQrValue"
        :is-dark-layout="isDarkLayout"
        @avatar-selected="onAvatarSelected"
      />

      <!-- ==================== TABS SECTION ==================== -->
      <b-card
        class="profile-tabs-card"
        no-body
      >
        <UserInfoTabs
          v-model="activeTab"
          :form="form"
          :is-saving="isSaving"
          :password-form="passwordForm"
          :show-pass="showPass"
          :show-saber-pass="showSaberPass"
          :update-profile="updateProfile"
          :update-password="updatePassword"
        />
      </b-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import ProfileHeader from './user-info/ProfileHeader.vue'
import UserInfoTabs from './user-info/UserInfoTabs.vue'

export default {
  components: {
    ProfileHeader,
    UserInfoTabs,
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      activeTab: 0,
      form: {
        token: '',
        name: '',
        email: '',
        phone: '',
        image: null,
        office_id: null,
        job_description: '',
        joined_at: '',
        whats_app: '',
        nationality: '',
        linked_in: '',
        passport_number: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        visa_issue_date: '',
        visa_expiry_date: '',
        labour_card_issue_date: '',
        labour_card_expiry_date: '',
        saber_email_dubai: '',
        saber_password_dubai: '',
        saber_email_manila: '',
        saber_password_manila: '',
        saber_email_hungary: '',
        saber_password_hungary: '',
      },
      avatarPreview: null,
      avatarFile: null,
      isUploadingAvatar: false,
      passwordForm: {
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      },
      showPass: {
        current: false,
        new: false,
        confirm: false,
      },
      showSaberPass: {
        dubai: false,
        manila: false,
        hungary: false,
      },
    }
  },
  computed: {
    initials() {
      if (!this.form.name) return '?'
      return this.form.name
        .split(' ')
        .map(w => w.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },
    isDarkLayout() {
      return document.querySelector('.dark-layout') != null
    },
    profileQrValue() {
      if (!this.form.token) return ''
      return `https://contact.gulftic.com/${this.form.token}`
    },
  },
  mounted() {
    this.getUserData()
  },
  methods: {
    onAvatarSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File Too Large',
            text: 'Image must be less than 5MB',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      this.avatarFile = file
      this.avatarPreview = URL.createObjectURL(file)
      this.uploadAvatar()
    },

    uploadAvatar() {
      if (!this.avatarFile) return
      this.isUploadingAvatar = true

      const formData = new FormData()
      formData.append('_method', 'PUT')
      formData.append('image', this.avatarFile)

      axios.post('user/profile/update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(res => {
          if (res.status === 200) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                text: 'Profile image updated',
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
            this.getUserData()
          }
        })
        .catch(err => {
          const message = err.response?.data?.message || 'Failed to upload image'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          this.avatarPreview = null
        })
        .finally(() => {
          this.isUploadingAvatar = false
          this.avatarFile = null
          this.$refs.avatarInput.value = ''
        })
    },

    getUserData() {
      this.isLoading = true
      axios.get('user/profile')
        .then(res => {
          if (res.status === 200) {
            const user = decryptData(res.data.payload)?.data?.user
            Object.keys(this.form).forEach(key => {
              if (user[key] !== undefined) {
                this.form[key] = user[key] || ''
              }
            })
            if (user.image) this.form.image = user.image
          }
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: 'Failed to load profile data',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    updateProfile() {
      this.isSaving = true

      const payload = {
        name: this.form.name,
        job_description: this.form.job_description,
        nationality: this.form.nationality,
        office_id: this.form.office_id,
        joined_at: this.form.joined_at,
        phone: this.form.phone,
        whats_app: this.form.whats_app,
        linked_in: this.form.linked_in,
        passport_number: this.form.passport_number,
        passport_issue_date: this.form.passport_issue_date,
        passport_expiry_date: this.form.passport_expiry_date,
        visa_issue_date: this.form.visa_issue_date,
        visa_expiry_date: this.form.visa_expiry_date,
        labour_card_issue_date: this.form.labour_card_issue_date,
        labour_card_expiry_date: this.form.labour_card_expiry_date,
        saber_email_dubai: this.form.saber_email_dubai,
        saber_password_dubai: this.form.saber_password_dubai,
        saber_email_manila: this.form.saber_email_manila,
        saber_password_manila: this.form.saber_password_manila,
        saber_email_hungary: this.form.saber_email_hungary,
        saber_password_hungary: this.form.saber_password_hungary,
      }

      axios.put('user/profile/update', payload)
        .then(res => {
          if (res.status === 200) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                text: 'Profile updated successfully',
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
            this.getUserData()
          }
        })
        .catch(err => {
          const message = err.response?.data?.message || 'Failed to update profile'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    updatePassword() {
      if (!this.passwordForm.current_password) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Current password is required',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      if (!this.passwordForm.new_password) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'New password is required',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      if (this.passwordForm.new_password !== this.passwordForm.new_password_confirmation) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Password confirmation does not match',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.isSaving = true

      const payload = {
        current_password: this.passwordForm.current_password,
        new_password: this.passwordForm.new_password,
        new_password_confirmation: this.passwordForm.new_password_confirmation,
      }

      axios.put('user/profile/update', payload)
        .then(res => {
          if (res.status === 200) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                text: 'Password updated successfully',
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
            this.passwordForm = {
              current_password: '',
              new_password: '',
              new_password_confirmation: '',
            }
          }
        })
        .catch(err => {
          const message = err.response?.data?.message || 'Failed to update password'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.isSaving = false
        })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/pages/user-profile/account.scss"></style>
